import { api, type SyncPayload } from './apiClient.js'
import { ApiError } from './errors.js'
import { db, ensureSchema, getMeta, setMeta, META_KEYS, type CacheRow } from './localDb.js'
import { counts, drain, nextWakeAt, prune, recoverInterrupted, type OutboxCounts } from './outbox.js'
import { drainPhotos, pendingCount, recoverInterruptedPhotos } from './photoQueue.js'

/**
 * Abgleichmotor – die einzige Stelle, die Netzaufrufe auslöst.
 *
 * Er tut genau drei Dinge:
 *   1. Einrichtung: einmal beim Anmelden den vollen Arbeitsbestand holen.
 *   2. Änderungsabgleich: danach nur noch die Unterschiede seit changeSeq.
 *   3. Ausgangskorb leeren.
 *
 * Ausgelöst wird er beim Start, beim Zurückwechseln zur App, bei Netzrückkehr
 * und alle drei Minuten im Vordergrund. Ist nichts geändert, ist die Antwort
 * wenige hundert Byte gross.
 */

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error'

export interface SyncStatus {
  state: SyncState
  online: boolean
  changeSeq: number
  lastSyncAt: number | null
  counts: OutboxCounts
  /** Fotos, die noch unterwegs sind – getrennt gezählt, getrennt angezeigt. */
  photosPending: number
  lastError: string | null
}

type Listener = (status: SyncStatus) => void

const listeners = new Set<Listener>()
let status: SyncStatus = {
  state: 'idle',
  online: true,
  changeSeq: 0,
  lastSyncAt: null,
  counts: { pending: 0, failed: 0, conflict: 0, unclear: 0, sending: 0 },
  photosPending: 0,
  lastError: null
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  listener(status)
  return () => listeners.delete(listener)
}

export function getStatus(): SyncStatus {
  return status
}

async function publish(patch: Partial<SyncStatus>): Promise<void> {
  status = {
    ...status,
    ...patch,
    counts: patch.counts ?? (await counts()),
    photosPending: patch.photosPending ?? (await pendingCount())
  }
  for (const l of listeners) l(status)
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false
}

// ---------------------------------------------------------------------------
// Daten in den Cache schreiben
// ---------------------------------------------------------------------------

const TABLE_FOR: Record<keyof Omit<SyncPayload, 'changeSeq' | 'bootstrap' | 'settings'>, string> = {
  customers: 'customers',
  projects: 'projects',
  projectMembers: 'projectMembers',
  catalogItems: 'catalogItems',
  rapports: 'rapports',
  documents: 'documents'
}

export async function applyPayload(payload: SyncPayload): Promise<void> {
  await db.transaction(
    'rw',
    [db.settings, db.customers, db.projects, db.projectMembers, db.catalogItems, db.rapports, db.documents, db.meta],
    async () => {
      if (payload.settings) {
        await db.settings.put({ id: 'tenant', changeSeq: payload.changeSeq, ...payload.settings })
      }
      for (const [key, table] of Object.entries(TABLE_FOR)) {
        const rows = payload[key as keyof SyncPayload] as Array<Record<string, unknown>> | undefined
        if (!rows || rows.length === 0) continue
        if (key === 'projectMembers') {
          await db.projectMembers.bulkPut(
            rows.map((r) => ({
              id: `${r.projectId as string}:${r.userId as string}`,
              projectId: r.projectId as string,
              userId: r.userId as string
            }))
          )
          continue
        }
        // Gelöschte Datensätze kommen mit deletedAt und werden entfernt.
        const gone = rows.filter((r) => r.deletedAt).map((r) => r.id as string)
        const live = rows.filter((r) => !r.deletedAt) as unknown as CacheRow[]
        if (gone.length) await db.table(table).bulkDelete(gone)
        if (live.length) await db.table(table).bulkPut(live)
      }
      await db.meta.put({ key: META_KEYS.changeSeq, value: payload.changeSeq })
      await db.meta.put({ key: META_KEYS.lastSyncAt, value: Date.now() })
    }
  )
}

/**
 * Serverantworten auf Schreibaktionen enthalten die geänderten Datensätze
 * bereits. Sie werden direkt in den Cache geschrieben – das spart je Aktion
 * einen Umlauf und die Oberfläche ist sofort aktuell.
 */
export async function applyActionResult(result: unknown): Promise<boolean> {
  if (!result || typeof result !== 'object') return false
  const r = result as Record<string, unknown>
  let touched = false

  const single: Array<[string, string]> = [
    ['customer', 'customers'],
    ['project', 'projects'],
    ['catalogItem', 'catalogItems'],
    ['rapport', 'rapports'],
    ['document', 'documents']
  ]
  for (const [key, table] of single) {
    const row = r[key]
    if (row && typeof row === 'object') {
      await db.table(table).put(row as CacheRow)
      touched = true
    }
  }

  if (typeof r.changeSeq === 'number') {
    const known = await getMeta<number>(META_KEYS.changeSeq, 0)
    // Nur vorrücken, wenn keine Lücke entsteht: sonst würde der nächste
    // Änderungsabgleich Datensätze überspringen, die andere Geräte erzeugt haben.
    if (r.changeSeq === known + 1) await setMeta(META_KEYS.changeSeq, r.changeSeq)
  }
  return touched
}

// ---------------------------------------------------------------------------
// Abläufe
// ---------------------------------------------------------------------------

export async function bootstrap(): Promise<SyncPayload> {
  await publish({ state: 'syncing' })
  const payload = await api.bootstrap()
  await applyPayload(payload)
  await publish({
    state: 'idle',
    changeSeq: payload.changeSeq,
    lastSyncAt: Date.now(),
    lastError: null
  })
  return payload
}

export interface SyncResult {
  changeSeq: number
  applied: number
  sent: number
  failed: number
}

/**
 * Läufe werden aneinandergereiht, nie überlappt.
 *
 * Drei Bildschirmaktionen kurz hintereinander lösten sonst drei Abgleiche
 * gleichzeitig aus: der Ausgangskorb schützt sich zwar selbst, aber
 * /sync/changes lief mehrfach parallel, und welcher Lauf den Zeiger zuletzt
 * schrieb, war Zufall. Genau so entstehen Lücken im Änderungsabgleich.
 */
let chain: Promise<SyncResult> = Promise.resolve({ changeSeq: 0, applied: 0, sent: 0, failed: 0 })
let queuedRuns = 0

export function syncNow(options: { force?: boolean } = {}): Promise<SyncResult> {
  // Mehr als ein wartender Lauf bringt nichts: der eine überträgt ohnehin
  // alles, was bis dahin aufgelaufen ist.
  if (queuedRuns >= 2 && options.force !== true) return chain
  queuedRuns++
  chain = chain
    .catch(() => ({ changeSeq: status.changeSeq, applied: 0, sent: 0, failed: 0 }))
    .then(() => runSync(options))
    .finally(() => {
      queuedRuns--
    })
  return chain
}

/** Wartet, bis kein Abgleich mehr läuft. Für Tests und für das Abmelden. */
export function syncSettled(): Promise<unknown> {
  return chain.catch(() => undefined)
}

async function runSync(options: { force?: boolean } = {}): Promise<SyncResult> {
  if (!isOnline()) {
    await publish({ state: 'offline', online: false })
    return { changeSeq: status.changeSeq, applied: 0, sent: 0, failed: 0 }
  }

  await publish({ state: 'syncing', online: true })
  let sent = 0
  let failed = 0
  let applied = 0
  let seq = await getMeta<number>(META_KEYS.changeSeq, 0)

  try {
    // Erst schreiben, dann lesen: so enthält der anschliessende Abgleich die
    // eigenen Änderungen bereits und die Oberfläche springt nicht zurück.
    const drained = await drain({ online: true })
    sent = drained.sent
    failed = drained.failed

    let needsPull = options.force === true || drained.results.length === 0
    for (const { result } of drained.results) {
      const touched = await applyActionResult(result)
      if (!touched) needsPull = true
    }

    seq = await getMeta<number>(META_KEYS.changeSeq, 0)
    if (needsPull || drained.results.length > 0) {
      const payload = seq === 0 ? await api.bootstrap() : await api.changes(seq)
      applied =
        payload.customers.length +
        payload.projects.length +
        payload.catalogItems.length +
        payload.rapports.length +
        payload.documents.length
      await applyPayload(payload)
      seq = payload.changeSeq
    }

    // Fotos NACH den Rapporten: ein Foto ohne seinen Rapport wird abgewiesen.
    // Und bewusst ohne await auf das Ergebnis der Anzeige – der Rapport gilt
    // als übertragen, sobald er beim Server ist, nicht erst mit dem letzten Bild.
    await drainPhotos({ online: true })

    await prune()
    await publish({ state: 'idle', changeSeq: seq, lastSyncAt: Date.now(), lastError: null })
    await scheduleRetryWake()
    return { changeSeq: seq, applied, sent, failed }
  } catch (err) {
    const message = err instanceof ApiError ? err.message : 'Abgleich fehlgeschlagen.'
    const offline = err instanceof ApiError && err.kind === 'network'
    await publish({ state: offline ? 'offline' : 'error', online: !offline, lastError: message })
    await scheduleRetryWake()
    return { changeSeq: seq, applied, sent, failed }
  }
}

// ---------------------------------------------------------------------------
// Auslöser
// ---------------------------------------------------------------------------

let interval: ReturnType<typeof setInterval> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let started = false
const INTERVAL_MS = 3 * 60_000

/**
 * Weckruf für den nächsten fälligen Wiederholversuch.
 *
 * Der Drei-Minuten-Takt ist für den Normalfall gedacht, nicht für einen
 * Eintrag, der in zwei Sekunden wieder dran wäre. Ohne diesen Wecker liegt
 * ein Rapport nach einem kurzen Netzhänger unnötig lange herum.
 */
async function scheduleRetryWake(): Promise<void> {
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  const at = await nextWakeAt()
  if (at === null) return
  const delay = Math.min(Math.max(at - Date.now(), 250), INTERVAL_MS)
  retryTimer = setTimeout(() => {
    retryTimer = null
    void syncNow()
  }, delay)
}

export interface StartOptions {
  intervalMs?: number
  /** In Tests abschaltbar. */
  attachBrowserEvents?: boolean
}

export async function start(options: StartOptions = {}): Promise<void> {
  if (started) return
  started = true

  const schema = await ensureSchema()
  // Unterbrochene Übertragungen zurücksetzen, bevor irgendetwas anderes läuft.
  await recoverInterrupted()
  await recoverInterruptedPhotos()
  const seq = await getMeta<number>(META_KEYS.changeSeq, 0)
  await publish({
    changeSeq: seq,
    online: isOnline(),
    lastSyncAt: await getMeta<number | null>(META_KEYS.lastSyncAt, null)
  })
  if (schema.cacheDropped) {
    // Der Cache wurde verworfen – der nächste Abgleich lädt ihn neu.
    await setMeta(META_KEYS.changeSeq, 0)
  }

  if (options.attachBrowserEvents !== false && typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void publish({ online: true }).then(() => syncNow())
    })
    window.addEventListener('offline', () => {
      void publish({ online: false, state: 'offline' })
    })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void syncNow()
    })
    window.addEventListener('focus', () => void syncNow())
  }

  interval = setInterval(() => {
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    if (!hidden) void syncNow()
  }, options.intervalMs ?? INTERVAL_MS)

  await syncNow()
}

export function stop(): void {
  if (interval) clearInterval(interval)
  if (retryTimer) clearTimeout(retryTimer)
  interval = null
  retryTimer = null
  started = false
  listeners.clear()
}

/** Für Tests: Zustand zurücksetzen, ohne die Datenbank anzufassen. */
export function resetForTests(): void {
  if (interval) clearInterval(interval)
  if (retryTimer) clearTimeout(retryTimer)
  chain = Promise.resolve({ changeSeq: 0, applied: 0, sent: 0, failed: 0 })
  queuedRuns = 0
  interval = null
  retryTimer = null
  started = false
  listeners.clear()
  status = {
    state: 'idle',
    online: true,
    changeSeq: 0,
    lastSyncAt: null,
    counts: { pending: 0, failed: 0, conflict: 0, unclear: 0, sending: 0 },
    photosPending: 0,
    lastError: null
  }
}
